import { useDispatch } from 'react-redux';
import { useCallback, useEffect, useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import { apiClient } from '../../../api/axios';
import { PaginationDefaults } from '../../../constants/defaults';

const fetchEvents = async ({ queryKey }) => {
  const [, params] = queryKey;
  const { data } = await apiClient.get(`/events`, { params });
  return data;
};

export const useGetEventsHandler = ({ currentQuery }) => {
  const LIMIT = PaginationDefaults.EVENTS_LIMIT;

  const dispatch = useDispatch();

  const [events, setEvents] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const queryClient = useQueryClient();

  const queryParams = {
    ...currentQuery,
    size: LIMIT,
    page: page,
  };

  if (currentQuery.status?.length) {
    queryParams.status = currentQuery.status.join(',');
  }

  if (currentQuery.event_id?.length) {
    queryParams.event_id = currentQuery.event_id;
  }

  const {
    data: fetchedData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['events', queryParams],
    queryFn: fetchEvents,
    gcTime: 10,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: 'always',
  });

  // Handle data updates for both initial fetch and load more
  useEffect(() => {
    if (!fetchedData) return;

    const newEvents = fetchedData.content || [];
    const hasMorePages = fetchedData.page_number + 1 < fetchedData.total_pages;

    if (page === 0) {
      setEvents(newEvents);
      setHasMore(hasMorePages);
    } else {
      setEvents((prevEvents) => [...prevEvents, ...newEvents]);
      setHasMore(hasMorePages);
      setIsLoadingMore(false);
    }
  }, [fetchedData, page]);

  useEffect(() => {
    if (isError) {
      setIsLoadingMore(false);

      if (error instanceof AxiosError) {
        if (error.response?.status === 400) {
          dispatch(showSnackbar({ message: 'Invalid Event ID', severity: 'error' }));
        } else if (error.response?.data?.message) {
          dispatch(showSnackbar({ message: error.response.data.message, severity: 'error' }));
        } else {
          dispatch(showSnackbar({ message: error.message, severity: 'error' }));
        }
      } else {
        dispatch(showSnackbar({ message: JSON.stringify(error), severity: 'error' }));
      }
    }
  }, [dispatch, error, isError]);

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || isLoading) return;

    setIsLoadingMore(true);
    setPage((prevPage) => prevPage + 1);
  }, [isLoadingMore, hasMore, isLoading]);

  const reset = useCallback(() => {
    setPage(0);
    setEvents([]);
    setHasMore(false);
    setIsLoadingMore(false);
    queryClient.invalidateQueries({ queryKey: ['events'] });
  }, [queryClient]);

  // Track previous query to avoid resetting on initial mount
  const query = JSON.stringify(currentQuery);
  const prevQueryRef = useRef(query);

  useEffect(() => {
    if (prevQueryRef.current !== query) {
      setPage(0);
      setEvents([]);
      prevQueryRef.current = query;
    }
  }, [query]);

  return {
    events,
    hasMore,
    isLoading: isLoading && page === 0,
    isLoadingMore,
    loadMore,
    reset,
  };
};
